/**
 * Identity auto-selection.
 *
 * Picking the wrong From: address on a reply is one of the most annoying bugs a
 * multi-alias mail client can have, so the resolver is a ranked list of rules and
 * — crucially — it reports WHICH rule fired. Deep mode shows that as a chip, so
 * the answer is always inspectable rather than mysterious.
 */

export interface Identity {
  readonly id: string
  readonly address: string
  readonly displayName?: string
  readonly signature?: string
  readonly isDefault?: boolean
  /** Prefer this identity for replies in these mailboxes. */
  readonly boundMailboxes?: readonly string[]
  /** Prefer this identity when corresponding with these recipient domains. */
  readonly boundDomains?: readonly string[]
}

export interface IncomingContext {
  /** Envelope recipient as recorded by the MDA. The most reliable signal. */
  readonly deliveredTo?: string
  readonly originalTo?: string
  readonly to?: readonly string[]
  readonly cc?: readonly string[]
  readonly mailbox?: string
  /** Domains of the people we would be replying to. */
  readonly recipientDomains?: readonly string[]
}

export type IdentityReason =
  | 'delivered-to'
  | 'x-original-to'
  | 'to'
  | 'cc'
  | 'plus-detail'
  | 'mailbox-binding'
  | 'domain-binding'
  | 'default'
  | 'first'

export interface IdentityChoice {
  readonly identity: Identity
  readonly reason: IdentityReason
  /** Human-readable explanation shown in deep mode. */
  readonly because: string
}

function bare(address: string): string {
  const match = /<([^>]+)>/.exec(address)
  return (match?.[1] ?? address).trim().toLowerCase()
}

/** Strip a +detail suffix: alice+shopping@example.com → alice@example.com */
export function stripPlusDetail(address: string, delimiter = '+'): string {
  const a = bare(address)
  const at = a.lastIndexOf('@')
  if (at < 0) return a
  const local = a.slice(0, at)
  const idx = local.indexOf(delimiter)
  return idx < 0 ? a : local.slice(0, idx) + a.slice(at)
}

export function selectIdentity(
  identities: readonly Identity[],
  context: IncomingContext,
  options: { readonly recipientDelimiter?: string } = {},
): IdentityChoice | null {
  if (identities.length === 0) return null
  const delimiter = options.recipientDelimiter ?? '+'
  const byAddress = new Map(identities.map((i) => [bare(i.address), i]))

  const exact = (address: string | undefined): Identity | undefined =>
    address ? byAddress.get(bare(address)) : undefined

  // 1-2. The envelope recipient is authoritative: it is the address the mail was
  //      actually delivered to, even when To: names a list or is missing entirely.
  //
  //      X-ORIGINAL-TO IS ASKED FIRST, AND THE ORDER IS THE WHOLE POINT OF THE
  //      PAIR. Postfix's cleanup records the recipient as it arrived, BEFORE any
  //      alias rewriting; Dovecot's Delivered-To holds the mailbox the mail
  //      finally landed in, AFTER it. For every address that is not a mailbox —
  //      a named alias, a disposable address — those two are different, and the
  //      one worth replying as is the one the correspondent actually wrote to.
  //      Asking Delivered-To first meant mail to `family@` and mail to
  //      `m.7fq2.petco.k3n9wq4x@` both resolved to the account identity, so the
  //      reply went out from the address the alias exists to keep out of it.
  //      They agree for ordinary mail, which is why this was invisible.
  const originalTo = exact(context.originalTo)
  if (originalTo) {
    return { identity: originalTo, reason: 'x-original-to', because: `X-Original-To was ${bare(context.originalTo!)}` }
  }
  const deliveredTo = exact(context.deliveredTo)
  if (deliveredTo) {
    return { identity: deliveredTo, reason: 'delivered-to', because: `Delivered-To was ${bare(context.deliveredTo!)}` }
  }

  // 3-4. Then the visible headers.
  for (const address of context.to ?? []) {
    const found = exact(address)
    if (found) return { identity: found, reason: 'to', because: `You were in To: as ${bare(address)}` }
  }
  for (const address of context.cc ?? []) {
    const found = exact(address)
    if (found) return { identity: found, reason: 'cc', because: `You were in Cc: as ${bare(address)}` }
  }

  // 5. A +detail address maps back to its base identity.
  for (const address of [context.originalTo, context.deliveredTo, ...(context.to ?? []), ...(context.cc ?? [])]) {
    if (!address) continue
    const stripped = stripPlusDetail(address, delimiter)
    const found = byAddress.get(stripped)
    if (found) {
      return { identity: found, reason: 'plus-detail', because: `${bare(address)} is a +detail form of ${stripped}` }
    }
  }

  // 6. Per-folder binding: mail filed to "Club" replies as the club address.
  if (context.mailbox) {
    const bound = identities.find((i) => i.boundMailboxes?.includes(context.mailbox!))
    if (bound) {
      return { identity: bound, reason: 'mailbox-binding', because: `${bound.address} is bound to the ${context.mailbox} folder` }
    }
  }

  // 7. Per-domain binding: always write to work contacts from the work address.
  for (const domain of context.recipientDomains ?? []) {
    const bound = identities.find((i) => i.boundDomains?.some((d) => d.toLowerCase() === domain.toLowerCase()))
    if (bound) {
      return { identity: bound, reason: 'domain-binding', because: `${bound.address} is bound to the ${domain} domain` }
    }
  }

  const fallback = identities.find((i) => i.isDefault)
  if (fallback) return { identity: fallback, reason: 'default', because: 'Your default identity' }
  return { identity: identities[0]!, reason: 'first', because: 'No default is set, so the first identity was used' }
}
